import { useEffect, useMemo, useRef } from 'react'
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useToast } from '../../../../contexts/ToastContext'
import { usePrescriptionsQuery } from '../../../../features/prescription'
import { queryKeys, resetPrescriptionsCache } from '../../../../shared'
import { Prescription } from '../../../../types/models/Presc'
import {
  actOnRemoteRequest,
  createRemoteRequest,
  getRemoteInputOverview,
  patchRemoteInputRoute,
} from './api'
import {
  doneMessage,
  hasStaleOwnPaper,
  indexOverview,
  keepRemoteImages,
  lookupPaper,
} from './model'
import { RemoteAction, RemoteInputOverview, RemoteRequest } from './types'

/** 住所は latest に閉じる(共有の queryKeys に足さない = ADR-0034) */
export const REMOTE_INPUT_QUERY_KEY = ['remoteInput', 'requests'] as const

/**
 * 5 秒。一覧の 2 秒(`usePrescriptionsQuery`)とは別の口で、依頼先の無い店でも空の応答を取りに行く。
 * 一覧の正典 30s からの逸脱は 04-apps/mamoru.md に登録する(頼まれた紙を待つ店が 30 秒は見ない)。
 * タブが hidden の間は TanStack Query の既定で通信しない。
 */
export const REMOTE_INPUT_POLL_MS = 5000

export const useRemoteInputQuery = () => {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: REMOTE_INPUT_QUERY_KEY,
    queryFn: async (): Promise<RemoteInputOverview> =>
      keepRemoteImages(
        queryClient.getQueryData<RemoteInputOverview>(REMOTE_INPUT_QUERY_KEY),
        (await getRemoteInputOverview()).data,
        Date.now()
      ),
    refetchInterval: REMOTE_INPUT_POLL_MS,
    staleTime: 0,
    refetchOnMount: true,
  })
}

/**
 * 画面が読む形。紙 1 枚ごとの出し分け(`lookup`)・頼まれた紙の行・新着の数。
 */
export const useRemoteInput = () => {
  const { data, isPending } = useRemoteInputQuery()
  const index = useMemo(() => indexOverview(data), [data])
  return {
    overview: data,
    isPending,
    lookup: (eventId: string) => lookupPaper(index, eventId),
    remoteRows: index.remoteRows,
    newCount: index.newCount,
  }
}

/**
 * 自店の一覧と 5 秒の口の食い違いを埋める。一覧の格納や紙の選択に関わらず効くよう App で 1 回だけ呼ぶ。
 *
 * - 自店の一覧が遠隔の店の直す前の版を持っていたら、一覧を全件取り直す。取り直している間は
 *   重ねて投げず、先に一覧の 2 秒の取得を止める(古い一覧を書き戻させない)
 * - 依頼先がオンの店で一覧に新しい紙が来たら、5 秒の口もすぐ取り直す。依頼の行は紙と同時に
 *   できるので、待つと 5 秒まで依頼の無い紙に見え、送信と「遠隔へ」が出てしまう
 */
export const useSyncOwnPapers = () => {
  const queryClient = useQueryClient()
  const { data: own } = usePrescriptionsQuery()
  const { data: overview } = useRemoteInputQuery()
  const isRefreshing = useRef(false)
  const newestEventId = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (isRefreshing.current || !hasStaleOwnPaper(overview, own)) return
    isRefreshing.current = true
    void queryClient
      .cancelQueries({ queryKey: queryKeys.prescriptions.list() })
      .then(() => resetPrescriptionsCache(queryClient))
      .finally(() => {
        isRefreshing.current = false
      })
  }, [overview, own, queryClient])

  const newest = own?.[0]?.ocr_request_event_id
  const isRouteOn = !!overview?.route?.is_on
  useEffect(() => {
    const previous = newestEventId.current
    newestEventId.current = newest
    if (!isRouteOn || previous === undefined || newest === previous) return
    void queryClient.invalidateQueries({ queryKey: REMOTE_INPUT_QUERY_KEY })
  }, [newest, isRouteOn, queryClient])
}

export type ActVars =
  | { action: 'request'; eventId: string; note: string }
  | {
      action: Exclude<RemoteAction, 'request'>
      request: RemoteRequest
      note: string
    }

/**
 * 操作。どれも札を進めるだけで送信しない(ボタンに副作用を持たせない)。
 * 通ったら toast、通らなくても toast にしない(行の中で言う。Note.tsx)。
 * どちらでも query を取り直す — 409 は「ほかの店が先に札を進めた」なので、行を今の札に揃える。
 */
export const useRemoteInputActions = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: REMOTE_INPUT_QUERY_KEY })

  const route = useMutation({
    mutationFn: (isOn: boolean) => patchRemoteInputRoute(isOn),
    onSuccess: ({ data }) =>
      queryClient.setQueryData<RemoteInputOverview>(
        REMOTE_INPUT_QUERY_KEY,
        (overview) => overview && { ...overview, route: data }
      ),
    onError: () => showToast('切り替えられませんでした。', 'error'),
    onSettled: refresh,
  })

  const act = useMutation({
    mutationFn: (vars: ActVars) =>
      vars.action === 'request'
        ? createRemoteRequest(vars.eventId, vars.note)
        : actOnRemoteRequest(vars.request.id, vars.action, vars.note),
    onSuccess: (_data, vars) => showToast(doneMessage(vars.action), 'success'),
    onSettled: refresh,
  })

  return {
    setRouteOn: (isOn: boolean) => route.mutate(isOn),
    /** 切り替えを送っている間は押せない(続けて押すと同じ値を 2 回送る) */
    isSwitchingRoute: route.isPending,
    /** 失敗は reject で返す(呼び出し側が行か dialog の中で言う) */
    act: async (vars: ActVars) => {
      await act.mutateAsync(vars)
    },
    isActing: act.isPending,
  }
}

/**
 * 頼まれた紙の最新版を、一覧のキャッシュと同じ要領で 1 件だけ差し替える(報告の旗の反映に使う)。
 * 頼まれた紙は自店の一覧(`queryKeys.prescriptions.list()`)には無く、ここにしか居ない。
 */
export const patchRemotePrescription = (
  queryClient: QueryClient,
  prescId: string,
  apply: (prescription: Prescription) => Prescription
) => {
  queryClient.setQueryData<RemoteInputOverview>(
    REMOTE_INPUT_QUERY_KEY,
    (overview) =>
      overview && {
        ...overview,
        as_remote: overview.as_remote.map((r) =>
          r.prescription.id === prescId
            ? { ...r, prescription: apply(r.prescription) }
            : r
        ),
      }
  )
}
